/**
 * Shared value formatting so grid cells, the record tree, profiles and chips all
 * describe the same value the same way.
 */

/** Only http(s) links are ever rendered as anchors. */
export function isHttpUrl(v: unknown): v is string {
  return typeof v === 'string' && /^https?:\/\/\S+$/i.test(v);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** Epoch ms for an ISO string or epoch-ms number; null when unparseable. */
export function toEpochMs(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && ISO_DATE.test(v)) {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** "YYYY-MM-DD HH:mm" in UTC; date-only when the time is exactly midnight. */
export function formatDate(ms: number): string {
  const iso = new Date(ms).toISOString();
  const day = iso.slice(0, 10);
  const time = iso.slice(11, 16);
  return time === '00:00' ? day : `${day} ${time}`;
}

/**
 * Large integers are identifiers (`sequenceId`, `id`), where thousands separators
 * only get in the way of copying and comparing.
 */
export function formatNumber(n: number): string {
  if (Number.isInteger(n) && Math.abs(n) >= 1e9) return String(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** Short label for an element of an array preview: its name/title/id, else first scalar. */
export function elementLabel(el: unknown): string {
  if (el === null || el === undefined) return 'null';
  if (typeof el !== 'object') return String(el);
  if (Array.isArray(el)) return `[${el.length}]`;
  const o = el as Record<string, unknown>;
  for (const k of ['name', 'title', 'value', 'label', 'id']) {
    const v = o[k];
    if (v !== null && v !== undefined && typeof v !== 'object') return String(v);
  }
  const first = Object.values(o).find((v) => v !== null && typeof v !== 'object');
  return first !== undefined ? String(first) : '{…}';
}

/** "3 · [Energy, Finance, …]" */
export function arrayPreview(arr: unknown[], maxItems = 3): string {
  if (arr.length === 0) return '0 · []';
  const shown = arr.slice(0, maxItems).map(elementLabel).join(', ');
  return `${arr.length} · [${shown}${arr.length > maxItems ? ', …' : ''}]`;
}

/** "{id, name, …}" */
export function objectPreview(obj: Record<string, unknown>, maxKeys = 4): string {
  const keys = Object.keys(obj);
  if (keys.length === 0) return '{}';
  return `{${keys.slice(0, maxKeys).join(', ')}${keys.length > maxKeys ? ', …' : ''}}`;
}

/** Text for the clipboard: strings verbatim, containers as pretty JSON. */
export function clipboardText(v: unknown): string {
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  if (v !== null && typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
}

export async function copyToClipboard(v: unknown): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(clipboardText(v));
    return true;
  } catch {
    return false;
  }
}

/** Badge text per column type. */
export const TYPE_BADGE: Record<string, string> = {
  number: 'num',
  text: 'txt',
  date: 'date',
  boolean: 'bool',
  array: 'arr',
  object: 'obj',
  mixed: 'mix',
};
